import {ReactNode} from 'react';

import {ColumnConstraint, TooltipIcon} from 'notebook/tables/constants/TableColumnConstants';
import {TABLE_COLUMN, TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeColumnHeaderIconPrefix,
  computeColumnIsOutdated,
  computeHasDestinationColumnMapping,
  computeIsSingleInputColumn,
  computeShouldHighlightColumnHeader,
} from 'notebook/tables/store/logic/ColumnHeaderLogic';
import {getTableCellAffixedProps} from 'notebook/tables/store/logic/TableAffixedLogic';
import {
  AxisCellAffixedLayout,
  AxisHeaderModel,
  createAxisHeaderIdentitySelector,
  createAxisIsSelectedSelector,
  createAxisIsTableSelectedSelector,
  createAxisSelectionRangeSelector,
  UNAFFIXED_LAYOUT,
} from 'notebook/tables/store/models/AxisHeaderModel';
import {TableModel} from 'notebook/tables/store/models/TableModel';
import {createTableStoreSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import TableUtil from 'notebook/tables/TableUtil';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * The model for a single column header cell, keyed by column index. Rendered by `TableHeaderRow`
 * for regular columns and by `TableSingleInput` for Single Input columns.
 *
 * This is the per-column counterpart to `RowModel`. Not to be confused with `TableColumnHeaderModel`
 * (`TableModel.columnHeader`), which holds the table-level configuration that every column header
 * shares.
 */
export interface ColumnHeaderModel extends AxisHeaderModel {
  dim: TableDimension.COLUMN;
  /** The editable name of the column, which renaming starts from. */
  label: string | null;
  /** The icon rendered before the label, or null when the column has no prefix icon. */
  iconPrefix: BenchlingIconName | null;
  /**
   * Icons rendered after the label, whose tooltips are also folded into the cell's tooltip. Only
   * supplied by standalone column headers; in-table columns take these from their column constraint.
   */
  tooltipIcons: Array<TooltipIcon> | null;
  /** Whether the header is highlighted as a user-added custom column. */
  shouldHighlightCell: boolean;
  /** Whether the column is backed by an outdated schema field. */
  columnIsOutdated: boolean;
  /**
   * The column's constraint, or null when the column has none (for example a plain table column or
   * a ghost cell past the end of the table).
   */
  columnConstraint: ColumnConstraint | null;
}

/**
 * Creates a selector for the {@link AxisCellAffixedLayout} of the column header at `index`. The
 * column is placed within the frozen columns by {@link getTableCellAffixedProps}, the same
 * derivation the data cells in that column use. Single Input columns are rendered outside the
 * scrollable table, so they are never affixed.
 */
function createColumnHeaderAffixedLayoutSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => AxisCellAffixedLayout {
  return createTableStoreSelector(
    {
      affixedColumnCount: (store) => tableSelector(store).affixed.affixedColumnCount,
      hasAnyFrozenColumns: (store) => tableSelector(store).affixed.hasAnyFrozenColumns,
      showAffixedShadow: (store) => tableSelector(store).affixed.showAffixedShadow,
      firstColumnWidth: (store) => tableSelector(store).measurements.firstColumnWidth,
      columnWidths: (store) => tableSelector(store).measurements.columnWidths,
      sliceStart: (store) => tableSelector(store).singleInputs.slices.start,
      singleInputBoundaryIndex: (store) => tableSelector(store).singleInputs.boundaryIndex,
    },
    ({singleInputBoundaryIndex, ...affixedConfig}): AxisCellAffixedLayout => {
      if (computeIsSingleInputColumn(index, singleInputBoundaryIndex)) {
        return UNAFFIXED_LAYOUT;
      }
      const affixed = getTableCellAffixedProps({j: index, ...affixedConfig});
      return {
        isAffixed: Boolean(affixed.isAffixed),
        affixedLeftSpacing: affixed.affixedLeftSpacing ?? 0,
        showAffixedShadow: Boolean(affixed.showAffixedShadow),
        isLastFrozenColumn: Boolean(affixed.isLastFrozenColumn),
        // The leading-column treatment belongs to the row axis; a column header is never the
        // table's leading column.
        isFirstColumn: false,
      };
    }
  );
}

/** Creates a selector for the column header at `index` against the table store. */
export function createColumnHeaderModelSelector(
  tableSelector: (store: TableEditableStore) => TableModel,
  index: number
): (store: TableEditableStore) => ColumnHeaderModel {
  const columnConstraintSelector = (store: TableEditableStore): ColumnConstraint | null =>
    tableSelector(store).props.table.columnConstraints?.[index] ?? null;

  // Each input is a value (or a stable object off table props) rather than the table itself, so
  // that editing an unrelated part of the table does not invalidate every column header's model.
  return createTableStoreSelector(
    {
      identity: createAxisHeaderIdentitySelector(tableSelector, TABLE_COLUMN, index),
      selectionRange: createAxisSelectionRangeSelector(tableSelector, TABLE_COLUMN, index),
      isSelected: createAxisIsSelectedSelector(tableSelector, TABLE_COLUMN, index),
      isTableSelected: createAxisIsTableSelectedSelector(tableSelector, TABLE_COLUMN),
      affixedLayout: createColumnHeaderAffixedLayoutSelector(tableSelector, index),
      columnConstraint: columnConstraintSelector,
      customLabel: (store) => TableUtil.getRenderedColumnLabel(tableSelector(store).props.table, index),
      label: (store) => TableUtil.getColumnLabel(tableSelector(store).props.table, index),
      tableKind: (store) => TableUtil.getTableKind(tableSelector(store).props.table),
      singleInputBoundaryIndex: (store) => tableSelector(store).singleInputs.boundaryIndex,
      highlightCustomColumns: (store) => store.editorContext.highlightCustomColumns,
      hideColumnHeaderIcons: (store) => tableSelector(store).props.hideColumnHeaderIcons,
      // The column mapping lookup reads global mappings state, so it is an input rather than part
      // of the memoized result.
      hasDestinationColumnMapping: (store) =>
        computeHasDestinationColumnMapping(
          tableSelector(store).props.fileId,
          tableSelector(store).props.table,
          columnConstraintSelector(store)
        ),
    },
    ({
      identity,
      selectionRange,
      isSelected,
      isTableSelected,
      affixedLayout,
      columnConstraint,
      customLabel,
      label,
      tableKind,
      singleInputBoundaryIndex,
      highlightCustomColumns,
      hideColumnHeaderIcons,
      hasDestinationColumnMapping,
    }): ColumnHeaderModel => {
      const columnDefinition = columnConstraint?.columnDefinition ?? null;
      return {
        ...identity,
        dim: TABLE_COLUMN,
        isHeaderCell: true,
        customLabel,
        selectionRange,
        isSelected,
        isTableSelected,
        affixedLayout,
        label,
        columnConstraint,
        iconPrefix: computeColumnHeaderIconPrefix({
          hideColumnHeaderIcons,
          hasDestinationColumnMapping,
          columnDefinition,
        }),
        tooltipIcons: null,
        shouldHighlightCell: computeShouldHighlightColumnHeader({
          isSingleInputColumn: computeIsSingleInputColumn(index, singleInputBoundaryIndex),
          highlightCustomColumns,
          columnDefinition,
          tableKind,
        }),
        columnIsOutdated: computeColumnIsOutdated(columnDefinition),
      };
    }
  );
}

/**
 * A {@link ColumnHeaderModel} for a column header rendered outside of any table store (for example
 * `SimpleTableAxisCell`). Such headers only ever label real columns, so they are never ghost cells
 * and never selected, and their label and icons come directly from their props rather than from a
 * table.
 */
export function makeStandaloneColumnHeaderModel({
  index,
  customLabel = null,
  label = null,
  tooltipIcons = null,
  numDims = 0,
}: {
  index: number;
  customLabel?: ReactNode;
  label?: string | null;
  tooltipIcons?: Array<TooltipIcon> | null;
  numDims?: number;
}): ColumnHeaderModel {
  return {
    dim: TABLE_COLUMN,
    index,
    isGhostCell: false,
    numDims,
    isHeaderCell: true,
    customLabel,
    selectionRange: null,
    isSelected: false,
    isTableSelected: false,
    affixedLayout: UNAFFIXED_LAYOUT,
    label,
    columnConstraint: null,
    iconPrefix: null,
    tooltipIcons,
    shouldHighlightCell: false,
    columnIsOutdated: false,
  };
}
